import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { DASH } from "@/constants/testIds";

const T = ({ active, payload, label, unit = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E5DE] p-3 font-mono-data text-xs">
      <div className="text-[#1A1B1A] font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex justify-between gap-6" style={{ color: p.color }}>
          <span>{p.name}</span>
          <span>{typeof p.value === "number" ? p.value.toFixed(2) : p.value}{unit}</span>
        </div>
      ))}
    </div>
  );
};

export function MonthlyKwhChart({ months }) {
  const data = months.map((m) => ({ month: m.month_label, kwh: m.kwh }));
  return (
    <div data-testid={DASH.monthlyChart} className="flat-card p-6 sm:p-8">
      <div className="overline mb-2">Chart</div>
      <h3 className="font-display text-xl tracking-tight mb-4">Monthly consumption (kWh)</h3>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#EDEDE8" vertical={false} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => `${v}`} width={40} />
          <Tooltip content={<T unit=" kWh" />} cursor={{ fill: "#F0F0EA" }} />
          <Bar dataKey="kwh" name="Usage" fill="#0F3524" barSize={32} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Co2TrendChart({ months }) {
  const data = months.map((m) => ({ month: m.month_label, co2: m.co2_kg }));
  return (
    <div data-testid={DASH.co2Chart} className="flat-card p-6 sm:p-8">
      <div className="overline mb-2">Chart</div>
      <h3 className="font-display text-xl tracking-tight mb-4">CO₂ emissions (kg)</h3>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#EDEDE8" vertical={false} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickLine={false} axisLine={false} width={44} />
          <Tooltip content={<T unit=" kg" />} />
          <Line
            type="linear"
            dataKey="co2"
            name="CO₂"
            stroke="#8FA38B"
            strokeWidth={2}
            dot={{ r: 4, fill: "#8FA38B" }}
            animationDuration={1500}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const BREAKDOWN_COLORS = ["#0F3524", "#8FA38B", "#B24F35", "#C9A464", "#6E716C"];

export function CostBreakdownChart({ months }) {
  if (!months.length) return null;
  const totals = months.reduce(
    (acc, m) => {
      acc.Generation += m.generation_rm || 0;
      acc.Network += m.network_rm || 0;
      acc.Retail += m.retail_rm || 0;
      acc.AFA += Math.max(0, m.afa_rm || 0);
      acc.KWTBB += m.kwtbb_rm || 0;
      return acc;
    },
    { Generation: 0, Network: 0, Retail: 0, AFA: 0, KWTBB: 0 }
  );
  const data = Object.entries(totals)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }));

  return (
    <div data-testid={DASH.breakdownChart} className="flat-card p-6 sm:p-8">
      <div className="overline mb-2">Chart</div>
      <h3 className="font-display text-xl tracking-tight mb-4">Cost breakdown (RM)</h3>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={50}
            outerRadius={90}
            paddingAngle={2}
            stroke="#F7F7F4"
            strokeWidth={2}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<T unit=" RM" />} />
          <Legend
            iconType="square"
            wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#6E716C" }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
